import React from 'react';
import { ChevronRight, Globe2 } from 'lucide-react';
import { Input } from '../../ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import TimezoneSelect from '../../TimezoneSelect';
import { formatGmtOffset, zoneOffsetMinutes } from '../../../lib/timezone';
import { DAYS, availabilityTimes, formatClock, formatDays } from '../bookingLinkForm';
import { FieldLabel, FieldHint, FieldError, errorProps } from './fields';

const zoneLabel = (zone) => (zone === 'UTC' ? 'UTC' : `${formatGmtOffset(zoneOffsetMinutes(zone))} ${zone}`);

/**
 * Step 2 — when people can book.
 *
 * #685: availability is ONE decision — days, window and the zone the window is
 * written in sit in one card, and the zone leads it. #579: those hours are the
 * OWNER's wall clock; the value is persisted and the server anchors the
 * availability window to it, so a wrong zone offers strangers the wrong hours.
 * The rarely-touched booking-window limits sit behind Advanced — disclosed,
 * not removed, and opened by the editor when a validation error lands there.
 */
export default function DateTimeSection({ form, setForm, errorFor, advancedOpen, setAdvancedOpen }) {
    const zone = form.timezone || 'UTC';
    const days = form.availability.days || [];
    const times = availabilityTimes(form.availability);
    const startValue = formatClock(times.startHour, times.startMin);
    const endValue = formatClock(times.endHour, times.endMin);

    const toggleDay = (day) => {
        setForm({
            ...form,
            availability: {
                ...form.availability,
                days: days.includes(day) ? days.filter(d => d !== day) : [...days, day].sort(),
            },
        });
    };

    const daysError = errorFor('bl-day-0');
    const endError = errorFor('bl-end-time');
    const maxDaysError = errorFor('bl-max-days');
    const minNoticeError = errorFor('bl-min-notice');

    return (
        <div className="space-y-5">
            <section aria-labelledby="bl-availability-heading" className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 space-y-4" data-testid="bl-availability-card">
                <div className="flex items-start gap-3">
                    <Globe2 className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                        <h3 id="bl-availability-heading" className="text-sm font-semibold text-stone-900">Availability</h3>
                        <p className="text-sm text-stone-600" data-testid="bl-availability-zone-banner">
                            Hours are in <strong className="text-stone-900">{zoneLabel(zone)}</strong>. Visitors are offered them converted to their own timezone.
                        </p>
                    </div>
                </div>

                {/* #579: the zone the availability hours are authored in — persisted
                    on the link; the server anchors the window to it and visitors see
                    it converted to their own zone. */}
                <div>
                    <FieldLabel htmlFor="booking-link-timezone">Availability timezone</FieldLabel>
                    <TimezoneSelect
                        id="booking-link-timezone"
                        value={zone}
                        onChange={tz => setForm({ ...form, timezone: tz })}
                        className="mt-1.5"
                    />
                    <FieldHint data-testid="availability-tz-help">
                        Your hours below are in this timezone. Visitors see them converted to their own.
                    </FieldHint>
                </div>

                <div>
                    {/* Not <label htmlFor>: a label pointing at a day button would
                        toggle that day whenever the label text is clicked. */}
                    <p id="bl-days-label" className="mb-1.5 text-[11px] font-semibold tracking-wider text-stone-500 uppercase">Available Days</p>
                    <div className="flex flex-wrap gap-2" role="group" aria-labelledby="bl-days-label" {...errorProps('bl-day-0', daysError)}>
                        {DAYS.map((d, i) => {
                            const on = days.includes(i);
                            return (
                                <button key={i} id={`bl-day-${i}`} type="button" onClick={() => toggleDay(i)} aria-pressed={on}
                                    className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${on ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-300'}`}
                                    data-testid={`bl-day-${i}`}>{d}</button>
                            );
                        })}
                    </div>
                    <FieldError id="bl-day-0" error={daysError} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <FieldLabel htmlFor="bl-start-time">Start Time</FieldLabel>
                        <Input id="bl-start-time" type="time" value={startValue}
                            onChange={e => { const [h, m] = e.target.value.split(':'); setForm({ ...form, availability: { ...form.availability, start_hour: parseInt(h), start_min: parseInt(m) } }); }}
                            className="mt-1.5 bg-white" data-testid="bl-start-time" />
                    </div>
                    <div>
                        <FieldLabel htmlFor="bl-end-time">End Time</FieldLabel>
                        <Input id="bl-end-time" type="time" value={endValue}
                            onChange={e => { const [h, m] = e.target.value.split(':'); setForm({ ...form, availability: { ...form.availability, end_hour: parseInt(h), end_min: parseInt(m) } }); }}
                            className="mt-1.5 bg-white" data-testid="bl-end-time" {...errorProps('bl-end-time', endError)} />
                        <FieldError id="bl-end-time" error={endError} />
                    </div>
                </div>

                <p className="text-xs font-medium text-stone-700 border-t border-violet-100 pt-3" data-testid="bl-availability-summary">
                    {formatDays(days)} · {startValue}–{endValue} · {zoneLabel(zone)}
                </p>
            </section>

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="rounded-xl border border-stone-200">
                <CollapsibleTrigger asChild>
                    <button type="button" className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="bl-advanced-toggle">
                        <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${advancedOpen ? 'rotate-90' : ''}`} aria-hidden="true" />
                        <span className="text-sm font-semibold text-stone-800">Advanced</span>
                        <span className="text-xs text-stone-500">Booking window and minimum notice</span>
                    </button>
                </CollapsibleTrigger>
                {/* forceMount keeps the fields in the DOM (their data-testids are
                    asserted, and validation must be able to reach them); `hidden`
                    does the collapsing. */}
                <CollapsibleContent forceMount hidden={!advancedOpen} className="px-4 pb-4" data-testid="bl-advanced-panel">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <FieldLabel htmlFor="bl-max-days">Max Days Ahead</FieldLabel>
                            <Input id="bl-max-days" type="number" value={form.max_days_ahead} onChange={e => setForm({ ...form, max_days_ahead: parseInt(e.target.value) || 60 })} className="mt-1.5" data-testid="bl-max-days" {...errorProps('bl-max-days', maxDaysError)} />
                            <FieldError id="bl-max-days" error={maxDaysError} />
                        </div>
                        <div>
                            <FieldLabel htmlFor="bl-min-notice">Min Notice (hours)</FieldLabel>
                            <Input id="bl-min-notice" type="number" value={form.min_notice_hours} onChange={e => setForm({ ...form, min_notice_hours: parseInt(e.target.value) || 1 })} className="mt-1.5" data-testid="bl-min-notice" {...errorProps('bl-min-notice', minNoticeError)} />
                            <FieldError id="bl-min-notice" error={minNoticeError} />
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
}
